'use client';

import React, { useEffect, useRef, useState } from 'react';
// Link handled by SectionHeader
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, Keyboard } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import ImageGalleryCard, { ImageGalleryCardProps } from './ImageGalleryCard';
import ImageGalleryCardSkeleton from '@/components/skeletons/ImageGalleryCard';
import SectionHeader from '@/components/common/SectionHeader';

// Runtime shape for an individual image inside a gallery
type ImageItem = { id?: number | string; title?: string; description?: string; src?: string; location?: string };
function extractSrc(it: any): string | null {
  try {
    if (!it) return null;
    if (typeof it === 'string') return it;
    const s = it.src ?? it.location ?? it.Location ?? it.thumbnailUrl ?? it.ThumbnailUrl ?? it.key ?? it.Key ?? it.path ?? it.Path;
    return typeof s === 'string' && s.trim() ? String(s) : null;
  } catch { return null; }
}

interface ImageGallerySliderProps {
  galleries: ImageGalleryCardProps[] | any[];
  slidesPerView?: number;
  loading?: boolean;
  placeholderCount?: number;
  variant?: 'default' | 'light';
  title?: string;
  subtitle?: string;
  badge?: string;
}

export default function ImageGallerySlider({ galleries, slidesPerView = 3, loading = false, placeholderCount = 6, variant = 'default', title = 'Image Galleries', subtitle, badge }: ImageGallerySliderProps) {
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);

  const input = Array.isArray(galleries) ? galleries : [];
  // Keep excluding galleries explicitly named "chat library" to avoid noise,
  // but do not drop galleries just because their first images are chat paths.
  const safeGalleries: ImageGalleryCardProps[] = input.filter((g) => {
    const name = String(g?.name ?? g?.Name ?? '').trim().toLowerCase();
    return !((name && name.includes('chat') && name.includes('library')) || name === 'chat-library');
  });

  const isLoading = loading || !Array.isArray(galleries) || galleries.length === 0;
  const usable = safeGalleries.length > 0 ? safeGalleries : input; // fallback to input if filter removed all
  const loop = !isLoading && usable.length > slidesPerView;

  // Responsive skeleton count to mirror layout
  const [cols, setCols] = useState(3);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq1 = window.matchMedia('(min-width: 1536px)');
    const mq2 = window.matchMedia('(min-width: 1280px)');
    const mq3 = window.matchMedia('(min-width: 768px)');
    // Match Swiper breakpoints: <768:1, 768-1535:2, >=1536:3
    const onChange = () => setCols(mq1.matches ? 3 : (mq3.matches ? 2 : 1));
    try { onChange(); mq1.addEventListener('change', onChange); mq2.addEventListener('change', onChange); mq3.addEventListener('change', onChange); }
    catch { mq1.addListener(onChange as any); mq2.addListener(onChange as any); mq3.addListener(onChange as any); }
    return () => { try { mq1.removeEventListener('change', onChange); mq2.removeEventListener('change', onChange); mq3.removeEventListener('change', onChange); }
      catch { mq1.removeListener(onChange as any); mq2.removeListener(onChange as any); mq3.removeListener(onChange as any); } };
  }, []);

  if (isLoading) {
    return (
      <section className="space-y-4" itemScope itemType="https://schema.org/ImageGallery">
        <SectionHeader title={title} subtitle={subtitle} badge={badge} href="/gallery" />
        <div className="relative overflow-hidden -mx-4 px-5 sm:mx-0 sm:px-0">
          <p className="sr-only" aria-live="polite">Loading galleries…</p>
          <div aria-hidden="true" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
            {Array.from({ length: cols }).map((_, i) => (
              <ImageGalleryCardSkeleton key={i} variant={variant} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Fallback removed: if everything filtered out, show input galleries rather than empty skeleton

  return (
    <section className="space-y-4" itemScope itemType="https://schema.org/ImageGallery">
      <SectionHeader title={title} subtitle={subtitle} badge={badge} href="/gallery" />
      {/* Microdata name for the gallery block */}
      <meta itemProp="name" content={title} />

      {/* Clip container and unify mobile gutters */}
      <div className="relative overflow-hidden -mx-2 px-3 sm:mx-0 sm:px-0">
        {/* ← Prev */}
        <button
          ref={prevRef}
          type="button"
          aria-label="Previous galleries"
          className="absolute top-1/2 -left-1 z-10 flex h-9 w-9
                     -translate-y-1/2 cursor-pointer items-center
                     justify-center rounded-full border border-border bg-background/90
                     text-muted-foreground shadow-sm backdrop-blur-sm
                     hover:bg-muted hover:text-foreground transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg"
               className="h-4 w-4" fill="none" viewBox="0 0 24 24"
               stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round"
                  strokeWidth={3} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* → Next */}
        <button
          ref={nextRef}
          type="button"
          aria-label="Next galleries"
          className="absolute top-1/2 -right-1 z-10 flex h-9 w-9
                     -translate-y-1/2 cursor-pointer items-center
                     justify-center rounded-full border border-border bg-background/90
                     text-muted-foreground shadow-sm backdrop-blur-sm
                     hover:bg-muted hover:text-foreground transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg"
               className="h-4 w-4" fill="none" viewBox="0 0 24 24"
               stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round"
                  strokeWidth={3} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <Swiper
          modules={[Navigation, Pagination, Autoplay, Keyboard]}
          slidesPerView={1}
          spaceBetween={16}
          loop={loop}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          pagination={{
            el: paginationRef.current,
            clickable: true,
            dynamicBullets: true,
          }}
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          keyboard={{ enabled: true, onlyInViewport: true }}
          breakpoints={{
            0:    { slidesPerView: 1 },
            640:  { slidesPerView: 1 },
            768:  { slidesPerView: 2 },
            1024: { slidesPerView: 2 },
            1280: { slidesPerView: 2 },
            1536: { slidesPerView },
          }}
          onBeforeInit={(swiper) => {
            // @ts-expect-error: Swiper types don't include these dynamic navigation refs
            swiper.params.navigation.prevEl = prevRef.current;
            // @ts-expect-error: Swiper types don't include these dynamic navigation refs
            swiper.params.navigation.nextEl = nextRef.current;
            // @ts-expect-error: Swiper types don't include these dynamic pagination refs
            swiper.params.pagination.el = paginationRef.current;
          }}
          className="overflow-visible"
        >
          {usable.map((gallery, idx) => (
            <SwiperSlide key={`${gallery?.galleryId ?? idx}-${idx}`}>
              <ImageGalleryCard {...(gallery as ImageGalleryCardProps)} />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* pagination dots BELOW (spacing unified via CSS var) */}
        <div ref={paginationRef} className="text-center swiper-pagination" />
      </div>
    </section>
  );
}
